import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import AccountCreationModal from "../../components/AccountCreationModal";
import { formatCurrency } from "../../utils/displayHelpers";
import { useCart } from "../../hooks/useCart";
import "./OrderSuccess.css";

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAccountModal, setShowAccountModal] = useState(false);

  useEffect(() => {
    if (sessionId) {
      fetchOrder();
      // Clear cart on success page (fallback for webhook in dev mode)
      // Clear immediately and refresh to ensure cart badge updates
      clearCart().then(() => {
        // Force a second clear to ensure cart is empty
        setTimeout(() => clearCart(), 500);
      });
    } else {
      setError("No session ID provided");
      setLoading(false);
    }
  }, [sessionId]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/session/${sessionId}`,
      );

      if (!response.ok) {
        throw new Error("Order not found");
      }

      const data = await response.json();
      setOrder(data);

      // Phase E3: Show account creation modal for guest orders
      // TEMPORARILY DISABLED: OAuth modal commented out
      // if (data.isGuestOrder && data.accountCreationToken && !data.accountCreationTokenUsed) {
      //   setTimeout(() => setShowAccountModal(true), 1500); // Show after 1.5s
      // }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="order-success-page">
          <div className="loading">Loading order details...</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <CustomerHeader />
        <div className="order-success-page">
          <div className="error-container">
            <h2>⚠️ Order Not Found</h2>
            <p>{error || "Unable to load order details"}</p>
            <button onClick={() => navigate("/")}>Return Home</button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="order-success-page">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h1>Order Confirmed!</h1>
          <p className="success-message">
            Thank you for your purchase. Your order has been confirmed and we'll
            send you an email confirmation shortly.
          </p>
          <p className="junk-mail-notice">
            📧 <strong>Didn't receive the email?</strong> Please check your
            junk/spam folder and mark it as "Not Spam" to ensure you receive
            future updates.
          </p>

          <div className="order-details">
            <div className="order-header">
              <div>
                <label>Order Number</label>
                <strong>{order.orderNumber}</strong>
              </div>
              <div>
                <label>Order Date</label>
                <strong>
                  {new Date(order.createdAt).toLocaleDateString()}
                </strong>
              </div>
            </div>

            <div className="customer-info-section">
              <h3>Customer Information</h3>
              <p>
                <strong>
                  {order.customerName.firstName} {order.customerName.lastName}
                </strong>
              </p>
              <p>{order.customerEmail}</p>
              <p>{order.customerPhone}</p>
            </div>

            <div className="order-items-section">
              <h3>Order Summary</h3>
              {order.shops.map((shop, index) => (
                <div key={index} className="shop-section">
                  <h4>{shop.shopName}</h4>

                  {shop.items && shop.items.length > 0 && (
                    <div className="items-list">
                      {shop.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="order-item">
                          <div className="item-info">
                            <span className="item-name">{item.name}</span>
                            <span className="item-quantity">
                              × {item.quantity}
                            </span>
                          </div>
                          <span className="item-price">
                            {formatCurrency(
                              item.priceAtPurchase * item.quantity,
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {shop.bookings && shop.bookings.length > 0 && (() => {
                    // Deduplicate bookings by appointment slot (same date + start
                    // time = same appointment). Keep the best-named entry; prefer
                    // entries with a specific service name over the "Service"
                    // fallback that may be stored for orphaned inline refs.
                    const slotMap = new Map();
                    for (const b of shop.bookings) {
                      const slot = `${b.appointmentDate || ""}|${b.startTime || ""}`;
                      const isFallback = !b.serviceName || b.serviceName === "Service";
                      if (
                        !slotMap.has(slot) ||
                        (!isFallback &&
                          (!slotMap.get(slot).serviceName ||
                            slotMap.get(slot).serviceName === "Service"))
                      ) {
                        slotMap.set(slot, b);
                      }
                    }
                    return (
                      <div className="bookings-list">
                        {Array.from(slotMap.values()).map((booking, bookingIndex) => (
                          <div key={bookingIndex} className="order-item">
                            <div className="item-info">
                              <span className="item-name">
                                {booking.serviceName || "Service"}
                              </span>
                              <span className="service-badge">Service</span>
                            </div>
                            {booking.bookingNumber && (
                              <div className="booking-number">
                                Booking #{booking.bookingNumber}
                              </div>
                            )}
                            <span className="item-price">
                              {formatCurrency(booking.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  <div className="shop-subtotal">
                    <span>Subtotal</span>
                    <strong>{formatCurrency(shop.subtotal)}</strong>
                  </div>
                </div>
              ))}

              <div className="order-total">
                {order.subtotal > 0 && (
                  <div className="total-row subtotal-row">
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                )}
                {order.taxAmount != null &&
                  order.taxAmount > 0 &&
                  order.taxRate != null && (
                    <div className="total-row tax-row">
                      <span>Tax</span>
                      <span>{formatCurrency(order.taxAmount)}</span>
                    </div>
                  )}
                <div className="total-row final-total">
                  <span>Total</span>
                  <strong>{formatCurrency(order.totalAmount)}</strong>
                </div>
              </div>
            </div>

            <div className="next-steps-section">
              <h3>What's Next?</h3>
              <ul>
                <li>
                  ✓ You'll receive an email confirmation at{" "}
                  {order.customerEmail}
                </li>
                {order.shops.some(
                  (s) => s.bookings && s.bookings.length > 0,
                ) && <li>✓ Your service appointments have been confirmed</li>}
                {order.shops.some((s) => s.items && s.items.length > 0) && (
                  <li>
                    ✓ You can pick up your products according to your selected
                    method
                  </li>
                )}
                <li>
                  ✓ Check your email for detailed pickup/appointment information
                </li>
              </ul>
            </div>
          </div>

          <div className="action-buttons">
            <button className="btn-primary" onClick={() => navigate("/")}>
              Continue Shopping
            </button>
            <button
              className="btn-secondary"
              onClick={() =>
                navigate(
                  `/track-order?email=${order.customerEmail}&order=${order.orderNumber}`,
                )
              }
            >
              Track Order
            </button>
          </div>
        </div>
      </div>

      {/* Phase E3: Account Creation Modal */}
      {/* TEMPORARILY DISABLED: OAuth modal commented out */}
      {/* {order && showAccountModal && (
        <AccountCreationModal
          isVisible={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          customerEmail={order.customerEmail}
          customerName={order.customerName}
          accountCreationToken={order.accountCreationToken}
        />
      )} */}

      <CustomerFooter />
    </>
  );
};

export default OrderSuccess;
